import { Component, inject, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule, Location } from '@angular/common';
import {
  FormBuilder,
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  UntypedFormArray,
  Validators
} from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import {
  ButtonComponent,
  FormFieldComponent,
  FormGroupComponent,
  FormRowComponent,
  IconComponent,
  LabelComponent,
  SubHeaderComponent,
  InputDirective,
  NotificationService
} from '@pansa/ui';
import { AuthorizedEntityService } from '../../services/authorized-entity.service';
import { AuthorizedPersonDTO, AuthorizedPersonForm } from '../../models/authorized-person.model';
import { FieldErrorComponent } from '@app/shared/components/field-error/field-error.component';

@Component({
  selector: 'rqa-add-authorized-person',
  templateUrl: './add-authorized-person.component.html',
  styleUrl: './add-authorized-person.component.scss',
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    ButtonComponent,
    IconComponent,
    LabelComponent,
    InputDirective,
    FormFieldComponent,
    FormRowComponent,
    FormGroupComponent,
    SubHeaderComponent,
    FieldErrorComponent
  ],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: true
})
export class AddAuthorizedPersonComponent {
  private readonly authorizedEntityService = inject(AuthorizedEntityService);
  private route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private fb = inject(FormBuilder);
  private location = inject(Location);
  private notificationService = inject(NotificationService)

  entityPersonForm!: FormGroup<AuthorizedPersonForm>;
  editMode: boolean = false;
  entityUuid: string = '';

  get phones(): UntypedFormArray {
    return this.entityPersonForm.get('phones') as UntypedFormArray;
  }

  ngOnInit(): void {
    this.buildForm();
    this.entityUuid = this.route.snapshot.paramMap.get('id') as string;
  }

  onSubmit() {
    this.entityPersonForm.markAllAsTouched();
    if (this.entityPersonForm.invalid) {
      return;
    }
    this.addAuthorizedPerson();
  }

  addPhone() {
    if (this.phones.controls.length <= 5) {
      this.phones.push(
        this.fb.control('', [
          Validators.required,
          Validators.pattern('^[+]?[0-9 ]{7,19}$'),
          Validators.minLength(7),
          Validators.maxLength(19)
        ])
      );
    }
  }

  removePhone(phoneValue: number) {
    this.phones.removeAt(phoneValue);
  }

  private addAuthorizedPerson() {
    const value = this.entityPersonForm.getRawValue();
    this.authorizedEntityService.addAuthorizedPerson(value, this.entityUuid).subscribe({
      next: (response) => {
        this.notificationService.success({ title: 'Sukces', subTitle: `Dodano osobę uprawnioną` });
        this.router.navigate(['/authorized-entity']);
        this.authorizedEntityService.refreshAuthorizedEntity();
      },
      error: (error) => {
        if (error.error) {
          console.log(error.error);
        }
      }
    })
  }

  onGoBack() {
    this.location.back();
  }

  private buildForm(): void {
    this.entityPersonForm = this.fb.nonNullable.group({
      firstName: this.fb.nonNullable.control('', {
        validators: [Validators.required]
      }),
      lastName: this.fb.nonNullable.control('', {
        validators: [Validators.required]
      }),

      email: this.fb.nonNullable.control('', {validators: [Validators.email]}),
      organization: this.fb.nonNullable.control(''),
      phones: this.fb.nonNullable.array<FormControl<string>>([this.fb.nonNullable.control('')]),
      additionalInfo: this.fb.nonNullable.control('')
    });
  }

  private setFormValues(authorizedEntity: AuthorizedPersonDTO): void {
    this.entityPersonForm.patchValue({
      firstName: authorizedEntity.firstName,
      lastName: authorizedEntity.lastName,
      email: authorizedEntity.email,
      organization: authorizedEntity.organization
    });

    const phonesArray = this.entityPersonForm.controls.phones;

    phonesArray.clear();

    authorizedEntity.phones.forEach((phone) => {
      phonesArray.push(
        this.fb.nonNullable.control(phone, {
          validators: [
            Validators.required,
            Validators.pattern('^[+]?[0-9 ]{7,19}$'),
            Validators.minLength(7),
            Validators.maxLength(19)
          ]
        })
      );
    });
  }
}
